import { useCallback } from 'react';
import { ApolloCache, FetchResult } from '@apollo/client';
import {
  TimeSignalClassification,
  TimeSignal,
  UpdateTimeSignalsByIdMutation
} from 'graphql/types';
import { useMeContext } from 'modules/me/MeContextProvider';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks';
import { useFeatureFlag } from 'modules/common/hooks';
import { useNotesToastContext } from '../NotesToastContextProvider';
import { clearCachedDates } from '../cache/utils/time-signal-utils';
import {
  NOTES_PAGE_TYPE,
  NotesClassification,
  useNotesFilterContext
} from '../NotesFilterContextProvider';
import { updateTimeSignalsCache } from '../cache/utils/cache-utils';
import { TIMELINE_TIMESIGNAL_COUNT } from '../Timeline';
import { truncateMessage } from '../common';
import {
  GetUpdateTimeSignalInput,
  useUpdateTimeSignalsById
} from './useUpdateTimeSignalsById';
import {
  TimeSignalGetParam,
  useTimeSignalGetParam
} from './useTimeSignalParam';

type OnBulkClassificationUpdateInput = {
  notes: TimeSignal[];
  updates: {
    classificationTarget: TimeSignalClassification;
  };
};

export const updateCache = (
  param: TimeSignalGetParam,
  selectedClassification: NotesClassification,
  classificationTarget: TimeSignalClassification,
  useLabelAcceptedBy: boolean
) => (
  cache: ApolloCache<UpdateTimeSignalsByIdMutation>,
  mutationResult: FetchResult<UpdateTimeSignalsByIdMutation>
): void => {
  if (mutationResult?.data?.updateTimeSignalsById?.items) {
    updateTimeSignalsCache(
      cache,
      mutationResult?.data?.updateTimeSignalsById?.items as TimeSignal[],
      param,
      selectedClassification,
      classificationTarget,
      useLabelAcceptedBy
    );
  }
};

export const getMoveMessage = (
  classificationTarget: TimeSignalClassification,
  notes: TimeSignal[]
): string =>
  notes.length === 1
    ? `toast.note.classification.singleMove.${classificationTarget.toLocaleLowerCase()}`
    : `toast.note.classification.bulkMove.${classificationTarget.toLocaleLowerCase()}`;

export const useBulkClassificationUpdate = (): {
  onBulkClassificationUpdate: (
    args: OnBulkClassificationUpdateInput
  ) => Promise<void>;
  updateInProgress: boolean;
} => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);

  const {
    filterContext: { pageType }
  } = useNotesFilterContext();
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );
  const {
    filterContext: { selectedClassification }
  } = useNotesFilterContext();

  const { updateTimeSignalsById, loading, error } = useUpdateTimeSignalsById();
  const { setToastContext } = useNotesToastContext();
  const { id: userUri } = useMeContext() ?? {};
  const onBulkClassificationUpdate = useCallback(
    async ({ notes, updates }: OnBulkClassificationUpdateInput) => {
      clearCachedDates(userUri);

      const newTimeSignals = notes.map((note: TimeSignal) =>
        GetUpdateTimeSignalInput({
          timeSignal: note,
          updates: {
            classificationUpdate: updates.classificationTarget
          },
          flags: {
            isSmartSuggestionEnabled: isSmartSuggestionEnabled,
            isTimeSignalFederatedSuggestionsEnabled: isTimeSignalFederatedSuggestionsEnabled,
            isEmptyLabelsEnabled: isEmptyLabelsEnabled
          }
        })
      );

      await updateTimeSignalsById({
        updates: newTimeSignals,
        awaitRefetchQueries: false,
        refetchQueries: ['timeSignalsCount', 'timeSignalsTotals'],
        update: updateCache(
          param,
          selectedClassification,
          updates.classificationTarget,
          isSmartSuggestionEnabled
        )
      });

      if (!loading && !error) {
        setToastContext({
          showSuccess: true,
          message: getMoveMessage(updates.classificationTarget, notes),
          values: {
            message: truncateMessage(
              notes.length === 1 ? notes?.[0].description ?? '' : ''
            ),
            count: `${notes.length}`
          }
        });
      } else if (error) {
        setToastContext({
          showError: true,
          message: 'toast.note.failure'
        });
      }
    },
    [
      userUri,
      updateTimeSignalsById,
      param,
      selectedClassification,
      isSmartSuggestionEnabled,
      loading,
      error,
      isTimeSignalFederatedSuggestionsEnabled,
      isEmptyLabelsEnabled,
      setToastContext
    ]
  );

  return {
    onBulkClassificationUpdate,
    updateInProgress: loading
  };
};
